/**
 * Helper: Strings
 * ------------------------------------------------------------------------------
 * - Useful utility string functions.
 * - Add new functions in alphabetical order.
 */

/**
 * Return a currency symbol for a given locale and currency code.
 * @param {String} locale - Locale code, e.g. fr-FR, en-GB.
 * @param {String} currency - Currency code, e.g. EUR, GBP.
 * @returns {String}
 */
export function getCurrencySymbol(locale, currency) {
  return (0).toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).replace(/\d/g, '').trim()
}

/**
 * Convert a given handle to pascal case.
 * @example:
 *  hello-world to HelloWorld
 * @param {String} handle handlelised string to be converted.
 * @returns {String}
 */
export function handleToPascalCase(handle) {
  const handleArray = handle.split('-')

  return handleArray.map((part) => {
    const firstLetter = part[0]
    const restOfWord = part.slice(1)

    return `${firstLetter.toUpperCase()}${restOfWord}`
  }).join('')
}

/**
 * Truncates string at a given character length, without
 * slicing a single word in half. Instead, rounds down to the closest space.
 * @example:
 *  const string = 'The quick brown fox jumps over the lazy dog'
 *  const shortened = truncateString(string, 12)
 *  Will output 'The quick' and not 'The quick br'
 *
 * @param {String} string - Given string to be shortened
 * @param {String|Number} maxLength - Maximum length of the string
 * @param {String} separator
 * @returns {String
 */
export function truncateString(string, maxLength = 999, separator = ' ') {
  if (string.length <= maxLength) {
    return string
  }

  return string.substr(0, string.lastIndexOf(separator, maxLength))
}

/**
 * Convert a given sentence to Title Case.
 * @example
 *  'hello world' to 'Hello World'
 * @param {String} string
 * @returns {String}
 */
export function stringToTitleCase(string = '') {
  if (!string || !string.length) {
    return ''
  }

  return string.toLowerCase().replace(/\w\S*/g, (word) => {
    return `${word.charAt(0).toUpperCase()}${word.slice(1)}`
  })
}

/**
 * Convert a string into handle (kebab-case).
 * - Sourced from https://gist.github.com/pablogiralt/f52d27428e7501909616d1dea04edfeb
 * @param {String} string - String to convert.
 * @returns {String}
 */
export function stringToHandle(string = '') {
  if (!string) {
    return ''
  }

  return string && string
    .normalize('NFD')
    .replace(/[[\]'()"]+/g, '') // Remove brackets and quotation marks
    .replace(/[\u0300-\u036f]/g, '') // Remove accent marks
    .replace(/([^\w]+|\s+)/g, '-') // Convert whitespace characters to hyphens
    .replace(/--+/g, '-') // Remove sequential hyphens
    .replace(/(^-+|-+$)/g, '') // Remove hyphens each end of the string
    .toLowerCase()
}

/**
 * Helper function that decodes the html from string
 *
 * @param {String} text - String containing encoded html
 */
export function decodeHTMLEntities(text) {
  if (!text) {
    return ''
  }

  const textArea = document.createElement('textarea')
  textArea.innerHTML = text
  return textArea.value
}

/**
 * Helper function that removes any content before the first <ul>
 * Introduced as 'Size & Fit' content on the PDP accordion should
 * be a <ul> followed by a string but one product was as per:
 * <p> </p><ul>...
 *
 * @param {String} str - String containing html
 */
export function stripBeforeFirstUl(str) {
  const regex = /.*?(<ul>)/s
  return str.replace(regex, '<ul>')
}

/**
 * Helper function that removes any html span tags from the string
 *
 * @param {String} text - String containing html
 */
export function removeListItemSpanTags(text) {
  if (!text) {
    return ''
  }
  const regex = /<span[^>]*>|<\/span>/gm
  return text.replace(regex, '')
}

/**
 * Method for validating email addresses. Used RegExp that matches strings
 * that contain a valid email as defined by the format `username@domain.com`
 *
 * - `^[\w.%+-]+`: starts with one or more word characters (letters, digits
 * or underscores) or one or more of the special characters `%`, `.`, `+` or `-`.
 * - `@`: followed by an `@` symbol.
 * - `[A-Za-z0-9.-]+`: followed by one or more letters, digits, periods
 * or hyphens (but not in the first or last position).
 * - `\.`: followed by a period.
 * - `[A-Za-z]{2,}$`: ends with two or more letters.
 *
 * @param {String} - Email value to be validated.
 * @returns {Boolean} - Is email valid or not.
 */
export function validateEmail(email) {
  const emailPattern = /^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/
  return emailPattern.test(email)
}

/**
 * Converts a kebab-case string to camelCase.
 * @param {string} kebabString - The kebab-case string to convert.
 * @returns {string} The camelCase version of the input string.
 */
export function kebabToCamel(kebabString) {
  return kebabString.replace(/-([a-z])/g, (match, char) => char.toUpperCase())
}

/* Takes a shopify data attribute and strips the initial "data-shopify-" part of
 * the string
 * @param {String} attribute
 * @returns {String}
 */
export function stripDataShopify(attribute) {
  return attribute.replace('data-shopify-', '')
}

/* Takes a string and unescapes the &quot; tokens into "s
 * @param {String} string
 * @returns {String}
 */
export function unescapeQuotes(string) {
  return string.replace(/&quot;/g, '"')
}

/* Takes a string and strips the first and last characters
 * @param {String} string
 * @returns {String}
 */
export function stripFirstAndLastCharacters(string) {
  return string.substring(1, string.length - 1)
}

/* Takes a shopify data attributes string and breaks it up into individual
 * attributes so that it can be used inside vue components
 * @param {String} shopifyAttributes
 * @returns {Object} - Object with key-value pairs for shopify attributes
 */
export function shopifyAttributesToJSON(shopifyAttributes) {
  if (Shopify?.designMode) {
    return shopifyAttributes.trim().split(' ').reduce((attrs, item) => {
      const [prop, val] = item.split('=')
      const propName = kebabToCamel(stripDataShopify(prop))
      const processedVal = stripFirstAndLastCharacters(unescapeQuotes(val))

      return val ? { ...attrs, [propName]: processedVal } : attrs
    }, {})
  } else {
    return {}
  }
}